import { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { useIsLicenseRedirectPending } from 'containers/Subscription/hooks';

import { AccountVerificationDialog } from '@identity/presentation/components/specific';
import {
  PROFILE_CHANGE_PASSWORD_ROUTE,
  PROFILE_VERIFY_PHONE_ROUTE,
  SIGN_OUT_ROUTE,
  VERIFY_ACCOUNT_ROUTE,
} from '@identity/presentation/constants';
import { useAccountVerificationGate } from '@identity/presentation/hooks';

/**
 * Where the dialog must never appear.
 *
 * It is a list of prefixes and not a `!pathname.startsWith('/verify-account')`. The gate runs over
 * the **whole** application, including the public routes that `AuthProvider` also wraps, and each
 * one of these has its own reason not to receive it. A named list is reviewable; a compound
 * condition hides them.
 */
const GATE_EXEMPT_PREFIXES = [
  '/signin',
  SIGN_OUT_ROUTE,
  '/logged-out',
  '/auth', // the public flows of identity: they carry their own credential
  '/legal',
  '/without-store',
  '/permissions-denied',
  '/onboarding', // the license watcher already owns this screen
  VERIFY_ACCOUNT_ROUTE, // the wizard itself
  // The two wizards of Mi perfil, and deliberately not `/profile`: with a pending requirement the
  // gate must keep pushing from anywhere in the application, and Mi perfil is one more place. These
  // two are flows of this same domain already under way, and a dialog on top of them would ask the
  // user to verify something while they are verifying it.
  PROFILE_VERIFY_PHONE_ROUTE,
  PROFILE_CHANGE_PASSWORD_ROUTE,
];

export type AccountVerificationGateProps = {
  /** Whether there is a Parrot session. With none there is nothing to ask about. */
  isAuthenticated: boolean;
};

/**
 * The only place that knows both sides: the identity domain and the license watcher.
 *
 * It lives in the App layer, outside every domain, which is what keeps the boundary of D5 intact —
 * `@reports/*` never imports `@identity/*`, and the assembly happens where both are known.
 *
 * **The license wins.** Subscription and onboarding are the two blocking redirects that already
 * exist after the login, and both come first: a dialog asking someone to verify their email on top
 * of an expired-license screen asks for something that unblocks nothing, and it covers the payment
 * screen while it does. The condition is not re-derived here — it is `isLicenseRedirectPending`,
 * the same function the watcher decides with, so the two cannot disagree.
 *
 * The three reasons not to ask are also three reasons not to render: an exempt route keeps the
 * dialog away even when the answer is already in the cache from a previous route.
 *
 * **`soft` postpones here, and only here.** The dialog reports that the user chose `Más tarde`; the
 * state that remembers it lives in this component, which `AuthProvider` keeps mounted for the whole
 * session, so the dialog does not come back on every route change. It is deliberately not persisted:
 * a soft mode is a reminder, and a reload is the point at which reminding again is right. `hard`
 * never receives `onDismiss`, so there is nothing to postpone and this state stays false.
 *
 * @param {AccountVerificationGateProps} props - Component properties
 * @example
 * <IdentityFlowProvider flow="verifyAccount">
 *   <AccountVerificationGate isAuthenticated={Boolean(tokens?.accessToken)} />
 * </IdentityFlowProvider>
 */
export const AccountVerificationGate = ({ isAuthenticated }: AccountVerificationGateProps) => {
  const history = useHistory();
  const { pathname } = useLocation();
  const isLicensePending = useIsLicenseRedirectPending();
  const [isPostponed, setIsPostponed] = useState(false);

  const isExempt = GATE_EXEMPT_PREFIXES.some((prefix) => pathname.startsWith(prefix));
  const isAllowedHere = isAuthenticated && !isExempt && !isLicensePending;

  const { shouldPrompt, isDismissible } = useAccountVerificationGate({ isEnabled: isAllowedHere });

  if (!isAllowedHere || !shouldPrompt || isPostponed) return null;

  return (
    <AccountVerificationDialog
      onDismiss={isDismissible ? () => setIsPostponed(true) : undefined}
      onSignOut={() => history.push(SIGN_OUT_ROUTE)}
      onVerify={() => history.push(VERIFY_ACCOUNT_ROUTE)}
    />
  );
};
